import React, { useState } from 'react';
import { X, Plus, AlertCircle } from 'lucide-react';

interface BulkCreateModalProps {
  onClose: () => void;
  onCreate: (hotelName: string, count: number, expiresInDays?: number) => void;
}

const BulkCreateModal: React.FC<BulkCreateModalProps> = ({ onClose, onCreate }) => {
  const [hotelName, setHotelName] = useState('');
  const [count, setCount] = useState(10);
  const [expiresInDays, setExpiresInDays] = useState<number | undefined>(undefined);
  const [useExpiration, setUseExpiration] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const validate = () => {
    const newErrors: { [key: string]: string } = {};

    if (!hotelName.trim()) {
      newErrors.hotelName = 'Hotel name is required';
    } else if (!/^[A-Za-z0-9\s]+$/.test(hotelName)) {
      newErrors.hotelName = 'Hotel name must contain only letters, numbers, and spaces';
    }

    if (count < 1) {
      newErrors.count = 'Count must be at least 1';
    } else if (count > 10000) {
      newErrors.count = 'Count cannot exceed 10,000';
    }

    if (useExpiration) {
      if (!expiresInDays || expiresInDays < 1) {
        newErrors.expiresInDays = 'Expiration days must be at least 1';
      } else if (expiresInDays > 3650) {
        newErrors.expiresInDays = 'Expiration days cannot exceed 3,650 (10 years)';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validate()) {
      onCreate(
        hotelName.trim(),
        count,
        useExpiration ? expiresInDays : undefined
      );
    }
  };

  const previewCode = hotelName.trim() 
    ? `${hotelName.toUpperCase()}_0000001` 
    : 'HOTELNAME_0000001';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Bulk Create QR Codes</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-600" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Hotel Name */}
          <div>
            <label htmlFor="hotelName" className="block text-sm font-medium text-gray-700 mb-2">
              Hotel Name
            </label>
            <input
              type="text"
              id="hotelName"
              value={hotelName}
              onChange={(e) => setHotelName(e.target.value)}
              placeholder="e.g., GrandHotel"
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.hotelName ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.hotelName && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <AlertCircle className="h-4 w-4 mr-1" />
                {errors.hotelName}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Only letters, numbers, and spaces allowed
            </p>
          </div>

          {/* Count */}
          <div>
            <label htmlFor="count" className="block text-sm font-medium text-gray-700 mb-2">
              Number of Codes
            </label>
            <input
              type="number"
              id="count"
              value={count}
              onChange={(e) => setCount(parseInt(e.target.value) || 0)}
              min="1"
              max="10000"
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.count ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.count && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <AlertCircle className="h-4 w-4 mr-1" />
                {errors.count}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Maximum 10,000 codes per batch
            </p>
          </div>

          {/* Expiration Toggle */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="useExpiration"
              checked={useExpiration}
              onChange={(e) => setUseExpiration(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="useExpiration" className="ml-2 text-sm text-gray-700">
              Set expiration date
            </label>
          </div>

          {/* Expiration Days */}
          {useExpiration && (
            <div>
              <label htmlFor="expiresInDays" className="block text-sm font-medium text-gray-700 mb-2">
                Expires In (Days)
              </label>
              <input
                type="number"
                id="expiresInDays"
                value={expiresInDays || ''}
                onChange={(e) => setExpiresInDays(parseInt(e.target.value) || undefined)}
                min="1"
                max="3650"
                placeholder="e.g., 365"
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.expiresInDays ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.expiresInDays && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.expiresInDays}
                </p>
              )}
            </div>
          )}

          {/* Preview */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm font-medium text-blue-900 mb-2">Code Format Preview:</p>
            <p className="text-lg font-mono text-blue-700">{previewCode}</p>
            <p className="text-xs text-blue-600 mt-2">
              Codes will be sequentially numbered from 0000001 to {String(count).padStart(7, '0')}
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create {count} Codes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BulkCreateModal;